import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { openLoginModal } from '../reduxtool/action';
import { Toast } from '../Marketing/Common/Toast';
import { decryptData } from '../Services/TabServices';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

interface DoctorLocation {
  locationId: number;
  locationName: string;
  hospitalName: string;
}
// Format date from "yyyy-mm-dd" → "dd-mm-yyyy" (for display only)
const formatDateForDisplay = (date: string): string => {
  if (!date) return "N/A";
  const [yyyy, mm, dd] = date.split("-");
  return `${dd}-${mm}-${yyyy}`;
};

// Format time from "HH:mm" (24hr) → "HH:mm AM/PM" (for display only)
const formatTimeForDisplay = (time: string): string => {
  if (!time) return "N/A";
  const [hourStr, minute] = time.split(":");
  const hour = parseInt(hourStr, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${String(displayHour).padStart(2, "0")}:${minute} ${ampm}`;
};

const toTitleCase = (name: string): string => {
  return name
    .trim()
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map(
      word => word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(" ");
};

interface DoctorAppointmentSlotsProps {
  doctor: any;
  selectedHospitalLocation?: DoctorLocation;
  onDoctorAvailabilityChange?: (isAvailable: boolean) => void;
  onclose?: () => void;
}

const DoctorAppointmentSlots: React.FC<DoctorAppointmentSlotsProps> = ({
  doctor,
  selectedHospitalLocation,
  onDoctorAvailabilityChange,
  onclose,
}) => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const doctorId = typeof doctor === 'string' ? Number(doctor) : doctor;
  const navigate = useNavigate();
  const dispatch: any = useDispatch();
  const token = sessionStorage.getItem('token');
  const storedUserId = sessionStorage.getItem('userId');
  const PatientId = storedUserId ? decryptData(storedUserId) : null;
  const doctorDetail = useSelector((state: any) => state.selectedDoctorDetails);

  const [selectedOption, setSelectedOption] = useState<any>('video');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<any>(null);
  const [selectedSlotId, setSelectedSlotId] = useState<any>(null);
  const [nextSevenDays, setNextSevenDays] = useState<{ date: string; day: string }[]>([]);
  const [doctorAvailability, setDoctorAvailability] = useState<any>(null);
  const [doctorAvailabilityFound, setDoctorAvailabilityFound] = useState<boolean>(true);
  const [hasAnyAvailabilityInNextSevenDays, setHasAnyAvailabilityInNextSevenDays] = useState<boolean>(false);
  const [hasAvailableSlots, setHasAvailableSlots] = useState<boolean>(false);
  const [checkoutPage, setCheckoutPage] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [slotError, setSlotError] = useState<string>('');
  const [slot, setSlots] = useState<any[]>([]);
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [loading, setLoading] = React.useState(false);
  const [callbackEmail, setCallbackEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  // Callback form states
  const [callbackName, setCallbackName] = useState('');
  const [callbackMobile, setCallbackMobile] = useState('');
  const [callbackConsent, setCallbackConsent] = useState(false);
  const [callbackLoading, setCallbackLoading] = useState(false);
  const [preferredDate, setPreferredDate] = useState('');
  const [preferredTime, setPreferredTime] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submittedData, setSubmittedData] = useState<any>({});
  const [nameError, setNameError] = useState('');
  const [mobileError, setMobileError] = useState('');
  const [dateError, setDateError] = useState('');
  const [timeError, setTimeError] = useState('');
  const [consentError, setConsentError] = useState('');



  const source = /Android|iPhone/i.test(navigator.userAgent)
    ? "OAS-Website-Mobile"
    : "OAS-Website-Desktop";
  const getMinDate = () => new Date().toISOString().split('T')[0];

  const getMaxDate = () => {
    const today = new Date();
    today.setDate(today.getDate() + 7);
    return today.toISOString().split('T')[0];
  };

  const validateName = (name: string): boolean => {
    if (!name.trim()) { setNameError('Name is required'); return false; }
    if (name.trim().length < 3) { setNameError('Name must be at least 3 characters long'); return false; }
    setNameError('');
    return true;
  };

  const validateEmail = (email: string): boolean => {

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    setEmailError('Enter a valid email address');
    return false;
  }
  setEmailError('');
  return true;
};
  const validateMobile = (mobile: string): boolean => {
    if (!mobile) { setMobileError('Mobile number is required'); return false;}
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(mobile)) {
      setMobileError('Enter valid 10-digit number');
      return false;
    }
    setMobileError('');
    return true;
  };

  const validateDate = (date: string): boolean => {
    if (!date) { setDateError('Visiting date is required'); return false; }
    setDateError('');
    return true;
  };

  const validateTime = (time: string): boolean => {
    if (!time) { setTimeError('Preferred time is required'); return false; }
    setTimeError('');
    return true;
  };

  const validateConsent = (consent: boolean): boolean => {
    if (!consent) { setConsentError('You must accept the terms and conditions'); return false; }
    setConsentError('');
    return true;
  };

  // ✅ FIXED: Dedicated mobile input handler — digits only, max 10, first digit 6–9
  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9]/g, ''); // digits only
    if (raw.length === 1 && !/^[6-9]$/.test(raw)) return; // first digit must be 6–9
    const v = raw.slice(0, 10); // max 10 digits
    setCallbackMobile(v);
    if (mobileError) validateMobile(v);
  };

  const isDayAvailable = (day: string): boolean => {
    if (!doctorAvailability) return false;
    const dayMapping: Record<string, string> = {
      Mon: 'monday', Tue: 'tuesday', Wed: 'wednesday',
      Thu: 'thursday', Fri: 'friday', Sat: 'saturday', Sun: 'sunday',
    };
    return doctorAvailability[dayMapping[day]] === 'Available';
  };

  const isSlotInFuture = (slotStartTime: string, date: string): boolean => {
    const now = new Date();
    const slotDateTime = new Date(`${date}T${slotStartTime}`);
    return slotDateTime.getTime() > now.getTime() + 10 * 60 * 1000;
  };

  // Fetch doctor availability
  useEffect(() => {
    const fetchDoctorAvailability = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/Patient/GetDoctorAvailability`);
        const data = response.data;
        const foundDoctor = data.find((doc: any) => doc.doctorId === doctorId);
        setDoctorAvailability(foundDoctor);
        const isAvailable = !!foundDoctor;
        setDoctorAvailabilityFound(isAvailable);
        if (onDoctorAvailabilityChange) onDoctorAvailabilityChange(isAvailable);
      } catch (err) {
        console.error('Error fetching doctor availability', err);
        setDoctorAvailabilityFound(false);
        if (onDoctorAvailabilityChange) onDoctorAvailabilityChange(false);
      }
    };
    fetchDoctorAvailability();
  }, [doctorId, onDoctorAvailabilityChange]);

  // Generate next 7 days
  useEffect(() => {
    const getNextSevenDays = (): { date: string; day: string }[] => {
      const daysArray: { date: string; day: string }[] = [];
      const options: Intl.DateTimeFormatOptions = { weekday: 'long' };
      for (let i = 0; i < 7; i++) {
        const currentDate = new Date();
        currentDate.setDate(currentDate.getDate() + i);
        daysArray.push({
          date: currentDate.toISOString().split('T')[0],
          day: new Intl.DateTimeFormat('en-US', options).format(currentDate).slice(0, 3),
        });
      }
      return daysArray;
    };

    const days = getNextSevenDays();
    setNextSevenDays(days);

    if (doctorAvailability) {
      const anyDayAvailable = days.some((day) => isDayAvailable(day.day));
      setHasAnyAvailabilityInNextSevenDays(anyDayAvailable);
      if (anyDayAvailable){
        const today = days[0];
        if (isDayAvailable(today.day)) {
          setSelectedDate(today.date);
        } else {
          const firstAvailableDay = days.find((day) => isDayAvailable(day.day));
          if (firstAvailableDay) setSelectedDate(firstAvailableDay.date);
        }
      }
    } else {
      setHasAnyAvailabilityInNextSevenDays(false);
    }
  }, [doctorAvailability]);

  // Fetch slots for selected date
  useEffect(() => {
    if (!selectedDate) return;
    setSlots([]);
    setError('');

    const getDoctorSlots = async () => {
      try {
        const res: any = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/Patient/GetDoctorSlotStatus`, {
          params: { doctorId, Date: selectedDate },
        });
        const slots = res?.data;
        setSlots(slots);
        if (slots && slots.length > 0) {
          const availableSlots = slots.filter((slot: any) =>
            slot.status !== 'Booked' &&
            slot.status !== 'Unavailable' &&
            isSlotInFuture(slot.slotStartTime, selectedDate!)
          );
          setHasAvailableSlots(availableSlots.length > 0);
          if (availableSlots.length > 0) {
            setSelectedSlot(availableSlots[0].slotStartTime);
            setSelectedSlotId(availableSlots[0].slotId);
          } else {
            setSelectedSlot(null);
            setSelectedSlotId(null);
          }
        } else {
          setHasAvailableSlots(false);
        }
      } catch (err: any) {
        console.error('GetDoctorSlot API error', err);
        setSlots([]);
        setHasAvailableSlots(false);
        setSlotError('No available slots or failed to fetch slots.');
      }
    };
    getDoctorSlots();
  }, [doctorId, selectedDate]);

  // Check patient slot availability
  useEffect(() => {
    if (!selectedSlotId || !selectedDate) return;

    const availabilitySlotPatient = async () => {
      setError('');
      if (!PatientId) return;
      try {
        await axios.get(`${process.env.REACT_APP_BASE_URL}/api/Patient/CheckAppointmentAvailabilityNew`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { PatientId, DoctorId: doctorId, SlotId: selectedSlotId, GivenDate: selectedDate },
        });
        setCheckoutPage(true);
      } catch (error: any) {
        if (error.response?.status === 409) {
          setError('You already have an appointment with another doctor at this time.');
          setCheckoutPage(false);
        } else {
          console.error('Error in getting slot availability for patient', error);
        }
      }
    };
    availabilitySlotPatient();
  }, [selectedSlotId]);

  const handleClick = () => {
    if (!token) { dispatch(openLoginModal()); return; }
    if (!selectedSlotId) return;
    if (!checkoutPage) return;
    navigate('/doctors-booking-appointment', {
      state: {
        doctorDetail, doctorId,
        bookingDate: selectedDate,
        bookingTime: selectedSlot,
        selectedSlotId,
        selectedHospitalLocation,
        hospitalName: selectedHospitalLocation?.hospitalName,
        locationName: selectedHospitalLocation?.locationName,
        locationId: selectedHospitalLocation?.locationId,
      },
    });
  };
const formatTimeForBackend = (time: string): string => {
  if (!time) return "";

  const [hourStr, minute] = time.split(":");
  let hour = parseInt(hourStr, 10);

  const ampm = hour >= 12 ? "PM" : "AM";

  hour = hour % 12;
  if (hour === 0) hour = 12;
  return `${hour}:${minute} ${ampm}`;
};

  const handleCallbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isNameValid = validateName(callbackName);
    const isMobileValid = validateMobile(callbackMobile);
   
    const isDateValid = validateDate(preferredDate);
    const isTimeValid = validateTime(preferredTime);
    const isConsentValid = validateConsent(callbackConsent);
    if (!isNameValid ||  !isMobileValid || !isDateValid || !isTimeValid || !isConsentValid) return;

    setCallbackLoading(true);
    if (!executeRecaptcha) {
      Toast.error('reCAPTCHA not ready. Please try again.');
      setLoading(false);
      return;
    }

    const recaptchaToken = await executeRecaptcha('callback_request');
    if (!recaptchaToken) {
      setError('Captcha verification failed. Please try again.');
      setLoading(false);
      return;
    }
    const formattedName = toTitleCase(callbackName);
// console.log("dep id",doctorDetail[0]?.departmentId )
    try {
      const apiPayload = {
        name: formattedName,
        mobileNo: callbackMobile,
        doctorId,
        emailId: callbackEmail,
        locationId: selectedHospitalLocation?.locationId || doctorDetail[0]?.locationId || 0,
        specializationId: Number(doctorDetail[0]?.departmentId || 0),
        isConsent: callbackConsent,
      preferredDate: preferredDate || null,          
  preferredTime: formatTimeForBackend(preferredTime),
        hospitalName: selectedHospitalLocation?.hospitalName || doctorDetail[0]?.hospitalName,
        locationName: selectedHospitalLocation?.locationName || doctorDetail[0]?.location,
        requestFor:"SA",
        recaptchaToken,
        source:source
      };
    const response=  await axios.post(`${process.env.REACT_APP_BASE_URL}/api/Patient/RequestCallBack`, apiPayload, {
        headers: { 'Content-Type': 'application/json' },
      });
       if (response.status === 200) {
                try {
          await axios.post(
             "https://integration.pqa.salesmax.ai/salesmax/leads?token=7MAcfjLhv7ITPcK_u2Eqxw",
            {
              name: formattedName,
        mobile: callbackMobile,
        doctorId,
        locationId: selectedHospitalLocation?.locationId || doctorDetail[0]?.locationId || 0,
        specializationId: Number(doctorDetail[0]?.departmentId || 0),
      preferredDate: preferredDate || null,          
  preferredTime: formatTimeForBackend(preferredTime),
        locationName: selectedHospitalLocation?.locationName || doctorDetail[0]?.location,
        source
            },
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          );
        } catch (webhookError) {
          console.error("Salesmax webhook failed:", webhookError);
        }
               
              } else {
                throw new Error("API error");
              }
     setSubmittedData({
  name: formattedName,
   email: callbackEmail,
  mobile: callbackMobile,
  date: formatDateForDisplay(preferredDate),  
  time: formatTimeForDisplay(preferredTime), 
  location: selectedHospitalLocation?.hospitalName ||"Not specified",
});
      setShowSuccessModal(true);
      setFormSubmitted(true);
      setCallbackEmail('');
setEmailError('');
      setCallbackName(''); setCallbackMobile(''); setCallbackConsent(false);
      setPreferredDate(''); setPreferredTime('');
      setNameError(''); setMobileError(''); setDateError(''); setTimeError(''); setConsentError('');
    } catch (error) {
      Toast.error('Submission failed. Please try again.');
      console.error('Callback submission error', error);
    } finally {
      setCallbackLoading(false);
    }
  };

  const generateTimeSlots = (startTime: string, endTime: string, interval: number) => {
    const slots: string[] = [];
    let current = new Date();
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    current.setHours(startHour, startMin, 0, 0);
    const endTimeObj = new Date();
    endTimeObj.setHours(endHour, endMin, 0, 0);
    while (current < endTimeObj) {
      slots.push(current.toTimeString().slice(0, 5));
      current.setMinutes(current.getMinutes() + interval);
    }
    return slots;
  };

  const dummySlots = generateTimeSlots('13:00', '16:30', 30);

  // ✅ Shared Mobile Input JSX — used in both Callback & Hospital Visit forms
  const renderMobileInput = (py: string = 'py-2') => (
    <div>
      <label className="block text-gray-700 text-xs font-medium mb-1">
        Mobile Number<span className="text-red-600">*</span>
      </label>
      <input
        type="text"
        inputMode="numeric"
        placeholder="Enter 10-digit mobile"
        value={callbackMobile}
        onChange={handleMobileChange}
        onBlur={() => validateMobile(callbackMobile)}
        maxLength={10}
        className={`w-full text-sm px-4 h-[30px]  rounded-lg border ${
          mobileError ? 'border-red-500' : 'border-gray-300'
        } bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-[#009A85] focus:border-transparent outline-none transition-all`}
        disabled={callbackLoading}
      />
      {mobileError && <p className="text-red-600 text-xs mt-1">{mobileError}</p>}
    </div>
  );

  // ─── Reusable Callback Form ───────────────────────────────────────────────
  const renderCallbackForm = () => (
    <>
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 transform transition-all animate-fadeIn">
            <div className="flex justify-center mb-2">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <div className="text-center mb-4">
              <h2 className="text-2xl font-bold text-[#184A84] mb-2"> Thank you!  </h2>
              <p className="text-gray-600 text-xs  text-start">
            
Your request for an appointment has been successfully submitted. Our executive will call you soon to assist you with this request.
<br /><span className="text-[#184A84] font-semibold">Note:</span>This is not a confirmation of your appointment and will not be considered as an appointment if you visit the OPD directly on the requested date.
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Name:</span>
                  <span className="font-semibold text-gray-900 capitalize">{submittedData.name || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Mobile:</span>
                  <span className="font-semibold text-gray-900">{submittedData.mobile || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Visiting Date:</span>
                  <span className="font-semibold text-gray-900">{submittedData.date || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Preferred Time:</span>
                  <span className="font-semibold text-gray-900">{submittedData.time || 'N/A'}</span>
                </div>
                {selectedHospitalLocation && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Location:</span>
                    <span className="font-semibold text-gray-900">{selectedHospitalLocation.hospitalName},&nbsp;{selectedHospitalLocation.locationName}</span>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={() => { setShowSuccessModal(false); if (onclose) onclose(); }}
              className="w-full bg-[#009A85] hover:bg-[#007a6a] text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-[1.02] active:scale-95 shadow-lg"
            >
              Close
            </button>
          </div>
        </div>
      )}
      
      <div className="flex justify-center">
        <div className="relative lg:shadow-lg bg-white sticky top-[184px] w-[350px] xs:w-[400px] sm:w-[400px] md:w-[400px] lg:w-[470px] xl:w-[450px] h-[470px] px-1 xs:px-4 py-8">
          <div className="flex justify-center">
            <p className="text-lg font-bold text-[#184A84]">Schedule Your Appointment</p>
          </div>
          <p className="text-sm text-gray-600 mt-2 text-center">Fill the details to schedule your appointment</p>

          {selectedHospitalLocation && (
            <div className="mb-3 rounded-lg px-3">
              <div className="flex items-center justify-center gap-2">
                <div className="p-1 rounded-full bg-white shadow-sm flex items-center justify-center">
                  <FaMapMarkerAlt className="w-3 h-3 text-[#0099A8]" />
                </div>
                <p className="text-xs font-semibold text-[#009A85]">
                  {selectedHospitalLocation.hospitalName}, {selectedHospitalLocation.locationName}
                </p>
              </div>
            </div>
          )}

          <form className="space-y-2 mt-4" onSubmit={handleCallbackSubmit}>
            {/* Name */}
            <div>
              <label className="block text-gray-700 text-xs font-medium mb-1">
                Your Name<span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter your name"
                value={callbackName}
                onChange={(e) => { setCallbackName(e.target.value); if (nameError) validateName(e.target.value); }}
                onBlur={() => validateName(callbackName)}
                className={`w-full text-sm capitalize px-4 h-[30px]  rounded-lg border ${nameError ? 'border-red-500' : 'border-gray-300'} bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-[#009A85] focus:border-transparent outline-none transition-all`}
                disabled={callbackLoading}
              />
              {nameError && <p className="text-red-600 text-xs mt-1">{nameError}</p>}
            </div>
            <div className="flex gap-2"><div>
  <label className="block text-gray-700 text-xs font-medium mb-1">
    Email Address
  </label>

  <input
    type="email"
    placeholder="Enter your email"
    value={callbackEmail}
    onChange={(e) => {
      setCallbackEmail(e.target.value);
      if (emailError) validateEmail(e.target.value);
    }}
    onBlur={() => validateEmail(callbackEmail)}
    className={`w-full px-4 text-sm h-[30px]  rounded-lg border ${
      emailError ? "border-red-500" : "border-gray-300"
    } bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-[#009A85] focus:border-transparent outline-none transition-all`}
    disabled={callbackLoading}
  />

  {emailError && (
    <p className="text-red-600 text-xs mt-1">{emailError}</p>
  )}
</div>
            {/* ✅ FIXED Mobile - Callback Form */}
            {renderMobileInput('py-2')}</div>


            <div className="flex gap-2 w-full">
              {/* Date */}
              <div className="flex-1">
                <label className="block text-gray-700 text-xs font-medium mb-1">
                  Visiting Date<span className="text-red-600">*</span>
                </label>
                <input
                  type="date"
                  value={preferredDate}
                  min={getMinDate()}
                  max={getMaxDate()}
                  onChange={(e) => { setPreferredDate(e.target.value); if (dateError) validateDate(e.target.value); }}
                  onBlur={() => validateDate(preferredDate)}
                  className={`w-full px-4 text-sm h-[30px]  rounded-lg border ${dateError ? 'border-red-500' : 'border-gray-300'} bg-white text-gray-900 focus:ring-2 focus:ring-[#009A85] focus:border-transparent outline-none transition-all`}
                  disabled={callbackLoading}
                />
                {dateError && <p className="text-red-600 text-xs mt-1">{dateError}</p>}
              </div>

              {/* Time */}
              <div className="flex-1">
                <label className="block text-gray-700 text-xs font-medium mb-1">
                  Preferred Time<span className="text-red-600">*</span>
                </label>
                <input
                  type="time"
                  value={preferredTime}
                  min="08:00"
max="16:00"
                  onChange={(e) => { setPreferredTime(e.target.value); if (timeError) validateTime(e.target.value); }}
                  onBlur={() => validateTime(preferredTime)}
                  className={`w-full text-sm px-4 h-[30px]  rounded-lg border ${timeError ? 'border-red-500' : 'border-gray-300'} bg-white text-gray-900 focus:ring-2 focus:ring-[#009A85] focus:border-transparent outline-none transition-all`}
                  disabled={callbackLoading}
                />
                {timeError && <p className="text-red-600 text-xs mt-1">{timeError}</p>}
              </div>
            </div>

            {/* Consent */}
            <div>
              <div className="flex items-start gap-2.5 pt-2">
                <input
                  type="checkbox"
                  id="callbackConsentCompact"
                  checked={callbackConsent}
                  onChange={(e) => { setCallbackConsent(e.target.checked); if (consentError) validateConsent(e.target.checked); }}
                  className="w-4 h-4 mt-0.5 accent-[#009A85] border-gray-300 rounded cursor-pointer"
                  disabled={callbackLoading}
                />
                <label htmlFor="callbackConsentCompact" className="text-gray-700 text-xs leading-relaxed cursor-pointer">
                  I agree to receive updates on WhatsApp and accept the{' '}
                  <Link to="/terms_conditions" className="text-[#009A85] font-semibold underline hover:text-[#007a6a]">
                    Terms & Conditions
                  </Link>
                </label>
              </div>
              {consentError && <p className="text-red-600 text-xs mt-1 ml-6">{consentError}</p>}
            </div>

            {/* Submit */}
            <div className="flex justify-center mt-3">
              <button
                type="submit"
                className={` bg-[#009A85] text-white px-4 py-2 rounded-lg text-sm text-[14px] bottom-4 transition-all duration-300 ${
                  !callbackName || !callbackMobile || !callbackConsent || !preferredDate || !preferredTime
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-[#009A85] hover:bg-[#007a6a]'
                }`}
                disabled={!callbackName || !callbackMobile || !callbackConsent || !preferredDate || !preferredTime || callbackLoading}
              >
                {callbackLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Submitting...
                  </span>
                ) : (
                  'Schedule Appointment'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );

  // ─── Hospital Visit Tab ───────────────────────────────────────────────────
  if (selectedOption === 'visit') {
    return (
      <div className="flex justify-center">
        <div className="relative lg:shadow-lg bg-white sticky top-[184px] w-[350px] xs:w-[400px] sm:w-[400px] md:w-[400px] lg:w-[470px] xl:w-[450px] px-1 xs:px-4 py-8 mt-5">
          <div className="flex justify-center">
            <p className="text-lg font-bold text-[#184A84]">Schedule Hospital Visit</p>
          </div>

          {/* Toggle */}
          <div className="flex items-center gap-2 xs:gap-4 text-sm pt-4 pb-4">
            <p
              role="button"
              aria-selected={selectedOption === 'video'}
              onClick={() => setSelectedOption('video')}
              className={`px-2 xs:px-4 sm:px-2 py-2 rounded-lg cursor-pointer transition-all duration-300 ${
                selectedOption === 'video' ? 'bg-[#009A8526] text-[#009A85] font-semibold shadow-md' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
              }`}
            >
              Video Consultation
            </p>
            <p
              role="button"
              aria-selected={selectedOption === 'visit'}
              onClick={() => setSelectedOption('visit')}
              className={`px-2 xs:px-4 py-2 rounded-lg cursor-pointer transition-all duration-300 ${
                selectedOption === 'visit' ? 'bg-[#009A8526] text-[#009A85] font-semibold shadow-md' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
              }`}
            >
              Hospital Visit
            </p>
          </div>

          {selectedHospitalLocation && (
            <div className="mb-3 rounded-lg px-3">
              <div className="flex items-center justify-center gap-2">
                <div className="p-1 rounded-full bg-white shadow-sm flex items-center justify-center">
                  <FaMapMarkerAlt className="w-3 h-3 text-[#0099A8]" />
                </div>
                <p className="text-xs font-semibold text-[#009A85]">
                  {selectedHospitalLocation.hospitalName}, {selectedHospitalLocation.locationName}
                </p>
              </div>
            </div>
          )}

          <form className="space-y-2" onSubmit={handleCallbackSubmit}>
            {/* Name */}
            <div>
              <label className="block text-gray-700 text-xs font-medium mb-1">
                Your Name<span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter your name"
                value={callbackName}
                onChange={(e) => { setCallbackName(e.target.value); if (nameError) validateName(e.target.value); }}
                onBlur={() => validateName(callbackName)}
                className={`w-full capitalize px-4  h-[30px] rounded-lg border ${nameError ? 'border-red-500' : 'border-gray-300'} bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-[#009A85] focus:border-transparent outline-none transition-all`}
                disabled={callbackLoading}
              />
              {nameError && <p className="text-red-600 text-xs mt-1">{nameError}</p>}
            </div>
<div>
  <label className="block text-gray-700 text-xs font-medium mb-1">
    Email Address
  </label>

  <input
    type="email"
    placeholder="Enter your email"
    value={callbackEmail}
    onChange={(e) => {
      setCallbackEmail(e.target.value);
      if (emailError) validateEmail(e.target.value);
    }}
    onBlur={() => validateEmail(callbackEmail)}
    className={`w-full px-4 h-[30px]  rounded-lg border ${
      emailError ? "border-red-500" : "border-gray-300"
    } bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-[#009A85] focus:border-transparent outline-none transition-all`}
    disabled={callbackLoading}
  />

  {emailError && (
    <p className="text-red-600 text-xs mt-1">{emailError}</p>
  )}
</div>
            {/* ✅ FIXED Mobile - Hospital Visit Form */}
            {renderMobileInput('py-1')}

            <div className="flex gap-2 w-full">
              {/* Date */}
              <div className="flex-1">
                <label className="block text-gray-700 text-xs font-medium mb-1">
                  Visiting Date<span className="text-red-600">*</span>
                </label>
                <input
                  type="date"
                  value={preferredDate}
                  min={getMinDate()}
                  max={getMaxDate()}
                  onChange={(e) => { setPreferredDate(e.target.value); if (dateError) validateDate(e.target.value); }}
                  onBlur={() => validateDate(preferredDate)}
                  className={`w-full px-4  h-[30px] rounded-lg border ${dateError ? 'border-red-500' : 'border-gray-300'} bg-white text-gray-900 focus:ring-2 focus:ring-[#009A85] focus:border-transparent outline-none transition-all`}
                  disabled={callbackLoading}
                />
                {dateError && <p className="text-red-600 text-xs mt-1">{dateError}</p>}
              </div>

              {/* Time */}
              <div className="flex-1">
                <label className="block text-gray-700 text-xs font-medium mb-1">
                  Preferred Time<span className="text-red-600">*</span>
                </label>
                <input
                  type="time"
                  value={preferredTime}
                  onChange={(e) => { setPreferredTime(e.target.value);
                    if (timeError) validateTime(e.target.value);
                  }}
                  onBlur={() => validateTime(preferredTime)}
                  className={`w-full px-4  h-[30px] rounded-lg border ${timeError ? 'border-red-500' : 'border-gray-300'} bg-white text-gray-900 focus:ring-2 focus:ring-[#009A85] focus:border-transparent outline-none transition-all`}
                  disabled={callbackLoading}
                />
                {timeError && <p className="text-red-600 text-xs mt-1">{timeError}</p>}
              </div>
            </div>

            {/* Consent */}
            <div>
              <div className="flex items-start gap-2.5 pt-2">
                <input
                  type="checkbox"
                  id="callbackConsentVisit"
                  checked={callbackConsent}
                  onChange={(e) => { setCallbackConsent(e.target.checked); if (consentError) validateConsent(e.target.checked); }}
                  className="w-4 h-4 mt-0.5 accent-[#009A85] border-gray-300 rounded cursor-pointer"
                  disabled={callbackLoading}
                />
                <label htmlFor="callbackConsentVisit" className="text-gray-700 text-xs leading-relaxed cursor-pointer">
                  I agree to receive updates on WhatsApp and accept the{' '}
                  <Link to="/terms_conditions" className="text-[#009A85] font-semibold underline hover:text-[#007a6a]">
                    Terms & Conditions
                  </Link>
                </label>
              </div>
              {consentError && <p className="text-red-600 text-xs mt-1 ml-6">{consentError}</p>}
            </div>

            {/* Submit */}
            <div className="flex justify-center mt-5">
              <button
                type="submit"
                className={`w-fit bg-[#009A85] text-white px-4 py-2 rounded-lg text-sm text-[14px] transition-all duration-300 ${
                  !callbackName || !callbackMobile || !callbackConsent || !preferredDate || !preferredTime
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-[#009A85] hover:bg-[#007a6a]'
                }`}
                disabled={  !callbackEmail ||!callbackName || !callbackMobile || !callbackConsent || !preferredDate || !preferredTime || callbackLoading}
              >
                {callbackLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Submitting...
                  </span>
                ) : (
                  'Schedule Hospital Visit'
                )}
              </button>
            </div>
          </form>

          {/* Success Modal */}
          {showSuccessModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 transform transition-all animate-fadeIn">
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                    <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-[#184A84] mb-3">Request Submitted Successfully!</h2>
                  <p className="text-gray-600 text-base leading-relaxed">
                    Thank you for your request. Our healthcare team will contact you soon.
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Name:</span>
                      <span className="font-semibold text-gray-900 capitalize">{submittedData.name || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Mobile:</span>
                      <span className="font-semibold text-gray-900">{submittedData.mobile || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Date:</span>
                      <span className="font-semibold text-gray-900">{submittedData.date || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Time:</span>
                      <span className="font-semibold text-gray-900">{submittedData.time || 'N/A'}</span>
                    </div>
                    {selectedHospitalLocation && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Location:</span>
                        <span className="font-semibold text-gray-900">{selectedHospitalLocation.hospitalName},&nbsp;{selectedHospitalLocation.locationName}</span>
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setShowSuccessModal(false)}
                  className="w-full bg-[#009A85] hover:bg-[#007a6a] text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-[1.02] active:scale-95 shadow-lg"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── Show Callback if no availability ────────────────────────────────────
  if (!doctorAvailabilityFound || !hasAnyAvailabilityInNextSevenDays) {
    return renderCallbackForm();
  }

  // ─── Default: Video Consultation with Slots ───────────────────────────────
  return (
    <div className="flex justify-center">
      <div className="relative lg:shadow-lg bg-white sticky top-[184px] w-[350px] xs:w-[400px] sm:w-[400px] md:w-[400px] lg:w-[470px] xl:w-[450px] h-[457px] px-1 xs:px-4 py-8 mt-5">
        <div className="flex justify-center">
          <p className="text-lg font-bold text-[#184A84]">Book Your Appointment</p>
        </div>

        <div className="flex items-center gap-2 xs:gap-4 text-sm pt-4">
          <p
            role="button"
            aria-selected={selectedOption === 'video'}
            onClick={() => setSelectedOption('video')}
            className={`px-2 xs:px-4 sm:px-2 py-2 rounded-lg cursor-pointer transition-all duration-300 ${
              selectedOption === 'video' ? 'bg-[#009A8526] text-[#009A85] font-semibold shadow-md' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
            }`}
          >
            Video Consultation
          </p>
          <p
            role="button"
            aria-selected={selectedOption === 'visit'}
            onClick={() => setSelectedOption('visit')}
            className={`px-2 xs:px-4 py-2 rounded-lg cursor-pointer transition-all duration-300 ${
              selectedOption === 'visit' ? 'bg-green-100 text-green-600 font-semibold shadow-md' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
            }`}
          >
            Hospital Visit
          </p>
        </div>

        {/* Date Picker */}
        <div>
          <p className="text-[14px] pt-4 pb-2">Choose Date</p>
          <div className="flex gap-2 xs:gap-2 sm:gap-4">
            {nextSevenDays.map((day, index) => {
              const isAvailable = isDayAvailable(day.day);
              return (
                <div
                  key={index}
                  onClick={() => { if (isAvailable) setSelectedDate(day.date); }}
                  style={{
                    backgroundColor: selectedDate === day.date ? '#009A8533' : 'white',
                    opacity: isAvailable ? 1 : 0.5,
                    pointerEvents: isAvailable ? 'auto' : 'none',
                  }}
                  className="w-[45px] h-[55px] border border-[#009A85] rounded-[5px] cursor-pointer"
                >
                  <div className="flex flex-col items-center">
                    <p className="text-[#009A85] text-[14px]">{day.date.slice(8, 10)}</p>
                    <p className="text-[#009A85] text-[14px]">{day.day}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Time Slots */}
        <div>
          <p className="text-[14px] pt-4 pb-2">Choose Time</p>
          {slot.length > 0 ? (
            <div className="grid grid-cols-5 xs:grid-cols-5 sm:grid-cols-6 md:grid-cols-6 lg:grid-cols-6 gap-3">
              {slot.map((slotTime: any, index: any) => {
                const isToday = selectedDate === new Date().toISOString().split('T')[0];
                const isFutureSlot = !isToday || isSlotInFuture(slotTime.slotStartTime, selectedDate!);
                const isAvailable = slotTime.status === 'Available' && isFutureSlot;
                return (
                  <div
                    key={index}
                    onClick={() => { if (isAvailable) { setSelectedSlot(slotTime.slotStartTime); setSelectedSlotId(slotTime.slotId); } }}
                    style={{
                      backgroundColor: selectedSlot === slotTime.slotStartTime ? '#184A8433' : 'white',
                      opacity: isAvailable ? 1 : 0.5,
                      borderColor: isAvailable ? '#184A84' : 'gray',
                      color: isAvailable ? '#184A84' : 'gray',
                      pointerEvents: isAvailable ? 'auto' : 'none',
                    }}
                    className="flex justify-center items-center w-[55px] h-[35px] border rounded-[5px] cursor-pointer"
                  >
                    <p>{slotTime.slotStartTime.slice(0, 5)}</p>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="grid grid-cols-5 xs:grid-cols-5 sm:grid-cols-6 md:grid-cols-6 lg:grid-cols-6 gap-3">
              {dummySlots.map((slotTime, index) => (
                <div
                  key={`dummy-${index}`}
                  style={{ backgroundColor: 'white', opacity: 0.9, borderColor: '#ccc', color: 'gray', pointerEvents: 'none' }}
                  className="flex justify-center items-center w-[55px] h-[35px] border rounded-[5px] cursor-not-allowed"
                >
                  <p>{slotTime}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {error ? (
          <p className="flex justify-center text-red-600 text-[10px] mt-2">{error}</p>
        ) : (
          <p className="text-[12px] mt-5">Please Choose slot wisely, as per your convenience</p>
        )}

        <div className="flex justify-center mt-3">
          <button
            onClick={handleClick}
            className="absolute bg-[#009A85] text-white px-4 py-2 rounded-lg text-sm text-[14px] bottom-4"
          >
            Book Appointment
          </button>
        </div>
      </div>
    </div>
  );
};

export default DoctorAppointmentSlots;
